import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

/**
 * Account/person icon in the top-right of the Header. Opens a small dropdown
 * with links to the four account sub-pages plus a Sign out affordance.
 * The whole menu closes on any outside click or Escape key.
 */
export default function AccountMenu({ iconBtnClass }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const linkCls = "block px-4 py-2.5 text-sm text-white/85 hover:bg-gold/10 hover:text-gold transition-colors";

  const onSignOut = async () => {
    setOpen(false);
    await logout();
    nav("/");
  };

  if (!user) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="header-account-btn"
        className={iconBtnClass}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          data-testid="header-account-menu"
          className="absolute right-0 mt-2 w-64 glass-strong border border-white/10 rounded-lg shadow-xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <div className="text-xs uppercase tracking-widest text-gold">Signed in as</div>
            <div className="text-sm font-serif mt-1 truncate" data-testid="account-menu-name">{user.name || user.email}</div>
            <div className="text-xs text-white/50 truncate">{user.email}</div>
          </div>
          <nav className="py-1">
            {/* Customer-facing account links — hidden for admins so their
                dropdown stays focused on admin surfaces (Admin panel,
                Dashboard, Sign out) and doesn't clutter with account
                pages that don't apply to their role. */}
            {user.role !== "admin" && (
              <>
                <Link to="/account" onClick={() => setOpen(false)} className={linkCls} data-testid="account-menu-profile">
                  My Account
                </Link>
                <Link to="/account/history" onClick={() => setOpen(false)} className={linkCls} data-testid="account-menu-history">
                  Purchase &amp; Auction History
                </Link>
                <Link to="/account/payments" onClick={() => setOpen(false)} className={linkCls} data-testid="account-menu-payment-history">
                  Payment History
                </Link>
                <Link to="/account/payments-due" onClick={() => setOpen(false)} className={linkCls} data-testid="account-menu-payments">
                  Payments Due
                </Link>
                <Link to="/me/evaluations" onClick={() => setOpen(false)} className={linkCls} data-testid="account-menu-evaluations">
                  Evaluations
                </Link>
              </>
            )}
            {user.role === "admin" && (
              <Link to="/admin" onClick={() => setOpen(false)} className={linkCls} data-testid="account-menu-admin">
                Admin
              </Link>
            )}
            <Link to="/dashboard" onClick={() => setOpen(false)} className={linkCls} data-testid="account-menu-dashboard">
              Full Dashboard
            </Link>
          </nav>
          <div className="border-t border-white/10 py-1">
            <button
              type="button"
              onClick={onSignOut}
              className="w-full text-left px-4 py-2.5 text-sm text-red-300 hover:bg-red-500/10 transition-colors"
              data-testid="account-menu-signout"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
